import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const InputForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    monthly_income: '',
    current_savings: '',
    current_debt: '',
    primary_goal: '',
    risk_preference: 'moderate'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(`/generate-plan`, {
        monthly_income: parseFloat(formData.monthly_income),
        current_savings: parseFloat(formData.current_savings),
        current_debt: parseFloat(formData.current_debt),
        primary_goal: formData.primary_goal,
        risk_preference: formData.risk_preference
      });

      if (response.data.saved_to_account) {
        toast.success('Your plan is ready and saved to your account');
        navigate('/dashboard');
      } else {
        navigate('/results', { state: { plan: response.data.plan, planId: response.data.plan_id } });
      }
    } catch (error) {
      console.error('Error generating plan:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'Free accounts include one plan. Upgrade to Premium for more.');
        if (user) navigate('/dashboard?premium=1');
      } else {
        toast.error('Failed to generate plan. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E5EB] py-4">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <h1 className="text-2xl font-black tracking-tighter cursor-pointer" onClick={() => navigate('/')} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Clarity
          </h1>
        </div>
      </nav>

      {/* Form Section */}
      <section className="py-24 sm:py-32">
        <div className="max-w-xl mx-auto w-full px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0B] mb-4 text-center">
              Let's build your plan
            </h2>
            <p className="text-base text-[#5C5C66] leading-relaxed mb-12 text-center">
              Just five quick questions to get started.
            </p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Monthly Income */}
              <div>
                <label className="block mb-3 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">
                  Monthly Income (RM)
                </label>
                <input
                  data-testid="input-income"
                  type="number"
                  name="monthly_income"
                  value={formData.monthly_income}
                  onChange={handleChange}
                  required
                  placeholder="5000"
                  className="w-full bg-white border border-[#E5E5EB] rounded-xl px-6 py-5 text-xl sm:text-2xl font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all placeholder:text-[#A1A1AA]"
                />
              </div>

              {/* Current Savings */}
              <div>
                <label className="block mb-3 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">
                  Current Savings (RM)
                </label>
                <input
                  data-testid="input-savings"
                  type="number"
                  name="current_savings"
                  value={formData.current_savings}
                  onChange={handleChange}
                  required
                  placeholder="10000"
                  className="w-full bg-white border border-[#E5E5EB] rounded-xl px-6 py-5 text-xl sm:text-2xl font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all placeholder:text-[#A1A1AA]"
                />
              </div>

              {/* Current Debt */}
              <div>
                <label className="block mb-3 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">
                  Current Debt (RM)
                </label>
                <input
                  data-testid="input-debt"
                  type="number"
                  name="current_debt"
                  value={formData.current_debt}
                  onChange={handleChange}
                  required
                  placeholder="0"
                  className="w-full bg-white border border-[#E5E5EB] rounded-xl px-6 py-5 text-xl sm:text-2xl font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all placeholder:text-[#A1A1AA]"
                />
              </div>

              {/* Primary Goal */}
              <div>
                <label className="block mb-3 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">
                  Primary Goal
                </label>
                <select
                  data-testid="input-goal"
                  name="primary_goal"
                  value={formData.primary_goal}
                  onChange={handleChange}
                  required
                  className="w-full bg-white border border-[#E5E5EB] rounded-xl px-6 py-5 text-xl sm:text-2xl font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all"
                >
                  <option value="">Select your goal</option>
                  <option value="Emergency Fund">Emergency Fund</option>
                  <option value="Pay off Debt">Pay off Debt</option>
                  <option value="Start Investing">Start Investing</option>
                  <option value="Save for something specific">Save for something specific</option>
                </select>
              </div>

              {/* Risk Preference */}
              <div>
                <label className="block mb-3 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">
                  Risk Preference
                </label>
                <select
                  name="risk_preference"
                  value={formData.risk_preference}
                  onChange={handleChange}
                  className="w-full bg-white border border-[#E5E5EB] rounded-xl px-6 py-5 text-xl sm:text-2xl font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all"
                >
                  <option value="conservative">Conservative</option>
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                </select>
              </div>

              {/* Submit Button */}
              <button
                data-testid="submit-plan-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#534AB7] text-white hover:bg-[#423B99] hover:-translate-y-0.5 transition-transform duration-200 rounded-xl px-8 py-4 font-semibold text-lg inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Generating your plan...' : 'Show My Plan'}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InputForm;